import {
    ConfirmRequestResult,
    CreateReservationResult,
    DeclineRequestResult,
    ReservationDTO,
    ReservationListFilter,
    Reservations,
    SaveRequestResult,
    UpdateConfirmedResult,
    UpdateReservationModel,
} from 'api/controllers/reservations';
import i18n from 'i18n-js';

import { isReservationFilterActive } from 'helpers/reservationHelper';
import { AppThunkAction, AppThunkActionAsync } from '../index';
import globalStore from '../globalStore';
import { ReservationsAction } from './actions';

let requestsLoad: { restaurantId: string; promise: Promise<boolean> } | null = null;
let requestsViewLoad = 0;
let requestsRevision = 0;
let availabilityLoad = 0;
let activeLoad = 0;
let activeRevision = 0;

export const actionCreators = {
    getRequests:
        (restaurantId: string): AppThunkActionAsync<ReservationsAction, boolean> =>
        (dispatch, getState) => {
            const isStillCurrent = () => getState?.().user.currentRestaurantId === restaurantId;
            const promise = Reservations.getRequests()
                .then((requests) => {
                    if (!isStillCurrent()) {
                        return false;
                    }
                    dispatch({
                        type: 'RESERVATION_REQUESTS_SET',
                        restaurantId,
                        requests,
                    });
                    return true;
                })
                .catch((e) => {
                    if (isStillCurrent()) {
                        globalStore.actionCreators.showToaster('error', e)(dispatch);
                    }
                    return false;
                })
                .finally(() => {
                    if (requestsLoad?.promise === promise) {
                        requestsLoad = null;
                    }
                });
            requestsLoad = { restaurantId, promise };
            return promise;
        },

    ensureRequests:
        (restaurantId: string): AppThunkActionAsync<ReservationsAction, boolean> =>
        (dispatch, getState) => {
            if (getState?.().reservations.requestsRestaurantId === restaurantId) {
                return Promise.resolve(true);
            }
            if (requestsLoad?.restaurantId === restaurantId) {
                return requestsLoad.promise;
            }
            return actionCreators.getRequests(restaurantId)(dispatch, getState);
        },

    selectRequest: (id: string | null): ReservationsAction => ({
        type: 'RESERVATION_REQUEST_SELECT',
        id,
    }),

    selectActive: (id: string | null): ReservationsAction => ({
        type: 'RESERVATION_ACTIVE_SELECT',
        id,
    }),

    getActiveReservations:
        (
            day: string | null,
            search = '',
            options: { keepPrevious?: boolean; isList?: boolean } = {}
        ): AppThunkActionAsync<ReservationsAction> =>
        (dispatch, getState) => {
            const load = ++activeLoad;
            const revision = activeRevision;
            const restaurantId = getState?.().user.currentRestaurantId;
            const isCurrent = () =>
                load === activeLoad && restaurantId === getState?.().user.currentRestaurantId;
            dispatch({
                type: 'RESERVATION_ACTIVE_LOAD',
                day,
                search,
                keepPrevious: !!options.keepPrevious,
            });
            return Reservations.getActiveReservations(day, search)
                .then((reservations) => {
                    if (!isCurrent()) {
                        return;
                    }
                    if (revision !== activeRevision) {
                        return actionCreators.getActiveReservations(day, search, {
                            ...options,
                            keepPrevious: true,
                        })(dispatch, getState);
                    }
                    dispatch({ type: 'RESERVATION_ACTIVE_SET', day, search, reservations });
                })
                .catch((e) => {
                    if (isCurrent()) {
                        dispatch({ type: 'RESERVATION_ACTIVE_FAIL' });
                        globalStore.actionCreators.showToaster(
                            'error',
                            options.isList ? i18n.t('reservations.filters.loadError') : e
                        )(dispatch);
                    }
                });
        },

    loadRequestsView:
        (filter: ReservationListFilter): AppThunkAction<ReservationsAction> =>
        (dispatch, getState) => {
            const load = ++requestsViewLoad;
            if (!isReservationFilterActive(filter)) {
                dispatch({ type: 'RESERVATION_REQUESTS_VIEW_SET', filter: null, requests: [] });
                return;
            }
            const revision = requestsRevision;
            const restaurantId = getState?.().user.currentRestaurantId;
            const isCurrent = () =>
                load === requestsViewLoad &&
                restaurantId === getState?.().user.currentRestaurantId;
            dispatch({ type: 'RESERVATION_REQUESTS_VIEW_LOAD' });
            Reservations.getRequests(filter)
                .then((requests) => {
                    if (!isCurrent()) {
                        return;
                    }
                    if (revision !== requestsRevision) {
                        actionCreators.loadRequestsView(filter)(dispatch, getState);
                        return;
                    }
                    dispatch({ type: 'RESERVATION_REQUESTS_VIEW_SET', filter, requests });
                })
                .catch(() => {
                    if (isCurrent()) {
                        dispatch({ type: 'RESERVATION_REQUESTS_VIEW_FAIL' });
                        globalStore.actionCreators.showToaster(
                            'error',
                            i18n.t('reservations.filters.loadError')
                        )(dispatch);
                    }
                });
        },

    getTablesAvailability:
        (
            start: string,
            durationMinutes: number,
            excludeReservationId: string | null
        ): AppThunkAction<ReservationsAction> =>
        (dispatch) => {
            const load = ++availabilityLoad;
            const key = [start, durationMinutes, excludeReservationId].join('|');
            dispatch({ type: 'RESERVATION_AVAILABILITY_LOAD', key });
            Reservations.getTablesAvailability(start, durationMinutes, excludeReservationId)
                .then((tables) => {
                    if (load === availabilityLoad) {
                        dispatch({ type: 'RESERVATION_AVAILABILITY_SET', key, tables });
                    }
                })
                .catch((e) => {
                    if (load === availabilityLoad) {
                        globalStore.actionCreators.showToaster('error', e)(dispatch);
                    }
                });
        },

    clearTablesAvailability: (): ReservationsAction => ({
        type: 'RESERVATION_AVAILABILITY_CLEAR',
    }),

    saveRequest:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, SaveRequestResult> =>
        async (dispatch) => {
            const result = await Reservations.saveRequest(id, model);
            if (result.status === 'saved') {
                dispatch({ type: 'RESERVATION_REQUEST_SAVED', reservation: result.reservation });
            } else {
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            }
            return result;
        },

    confirmRequest:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, ConfirmRequestResult> =>
        async (dispatch) => {
            const result = await Reservations.confirmRequest(id, model);
            if (result.status === 'confirmed') {
                activeRevision++;
                dispatch({ type: 'RESERVATION_CONFIRMED', reservation: result.reservation });
            } else if (result.status === 'alreadyHandled') {
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            }
            return result;
        },

    declineRequest:
        (id: string): AppThunkActionAsync<ReservationsAction, DeclineRequestResult> =>
        async (dispatch, getState) => {
            const result = await Reservations.declineRequest(id);
            if (result === 'declined') {
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            } else if (getState) {
                await actionCreators.getRequests(getState().user.currentRestaurantId)(
                    dispatch,
                    getState
                );
            }
            return result;
        },

    updateConfirmed:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, UpdateConfirmedResult> =>
        async (dispatch, getState) => {
            const result = await Reservations.updateConfirmed(id, model);
            if (result.status === 'updated') {
                actionCreators.applyRealtimeUpdate(result.reservation)(dispatch, getState);
            }
            return result;
        },

    createReservation:
        (
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, CreateReservationResult> =>
        async (dispatch) => {
            const result = await Reservations.createReservation(model);
            if (result.status === 'created') {
                activeRevision++;
                dispatch({ type: 'RESERVATION_CONFIRMED', reservation: result.reservation });
            }
            return result;
        },

    markNoShow:
        (id: string): AppThunkActionAsync<ReservationsAction, void> =>
        async (dispatch, getState) => {
            try {
                const reservation = await Reservations.markNoShow(id);
                actionCreators.applyRealtimeUpdate(reservation)(dispatch, getState);
            } catch (error) {
                try {
                    const current = await Reservations.getById(id);
                    actionCreators.applyRealtimeUpdate(current)(dispatch, getState);
                } catch (refreshError) {
                    globalStore.actionCreators.showToaster('error', refreshError)(dispatch);
                }
                throw error;
            }
        },

    applyRealtimeUpdate:
        (reservation: ReservationDTO): AppThunkAction<ReservationsAction> =>
        (dispatch, getState) => {
            if (getState && getState().user.currentRestaurantId !== reservation.restaurantId) {
                return;
            }
            activeRevision++;
            requestsRevision++;
            dispatch({ type: 'RESERVATION_REALTIME_UPDATE', reservation });
            const key = getState?.().reservations.availability.key;
            if (key) {
                const [start, duration, excludeId] = key.split('|');
                actionCreators.getTablesAvailability(
                    start,
                    Number(duration),
                    excludeId || null
                )(dispatch, getState);
            }
        },
};
